import { AnnouncementFormValues } from '../types';
import { Controller, useFormContext } from 'react-hook-form';
import { SVG } from '../../../../../../constants/assetLists';
import { Checkbox } from '@mui/material';
import AppButton from '../../../../../../components/common/AppButton';
import SearchIcon from '@mui/icons-material/Search';
import React, { useRef, useEffect } from 'react';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AppMUIAutocomplete from '../../../../../../components/common/AppMUIAutoComplete';
import { useQuery } from '@tanstack/react-query';
import QUERY_KEYS from '../../../../../../constants/queryKeys';
import { handleGetAllChecklistService } from '../../../../../../services/operations/checklist';
import {
  getCorrectPath,
  getDepartmentFilterParams,
  hasPermission,
} from '../../../../../../utils';
import { useOperationsContext } from '../../../../../../context/OperationsContextProvider';
import AddEditCheckList from '../../AddEditCheckList';
import { useClientsContext } from '../../../../../../context/clients/ClientsContextProvider';
import { ICONS } from '../../../../../users/data';

type Props = {
  forForm: 'announcement' | 'task';
  index?: number;
  handleExpandSection: (section: string) => void;
  expandedSections: string[];
};

function Checklist({
  forForm,
  index,
  handleExpandSection,
  expandedSections,
}: Props) {
  const { control, setValue, watch, trigger } =
    useFormContext<AnnouncementFormValues>();

  const { setOpenAddCheckListDialog, newChecklistId, setNewChecklistId } =
    useOperationsContext();
  const [selectedChecklist, setSelectedChecklist] = React.useState<
    string | null
  >(null);
  const [expandedDepartments, setExpandedDepartments] = React.useState(
    new Set(),
  );
  const checklistRef = useRef<HTMLDivElement>(null);
  const { selectedClient, selectedLocation } = useClientsContext();

  const isClientSelected = selectedLocation?.isClient;
  const isBranchSelected = selectedLocation?.isBranch;
  const isSubBranchSelected = selectedLocation?.isSubBranch;

  const selectedBranchId: string[] = isClientSelected
    ? watch('branchIds')
    : isBranchSelected && selectedLocation?.id
      ? [selectedLocation.id]
      : isSubBranchSelected && selectedLocation?.branchId
        ? [selectedLocation.branchId]
        : [];
  const selectedSubBranchId: string[] = isBranchSelected
    ? watch('subBranchIds')
    : isSubBranchSelected && selectedLocation?.id
      ? [selectedLocation.id]
      : [];

  const { data: checklistData } = useQuery({
    queryKey: [
      QUERY_KEYS.GET_ALL_CHECKLISTS,
      selectedClient?._id,
      selectedBranchId,
      selectedSubBranchId,
    ],
    queryFn: () =>
      handleGetAllChecklistService({
        groupByDepartment: true,
        clientId: selectedClient?._id,
        ...(selectedBranchId.length > 0 && { branchId: selectedBranchId }),
        ...(selectedSubBranchId.length > 0 && {
          subBranchId: selectedSubBranchId,
        }),
        locationStatus: 'active',
        ...getDepartmentFilterParams(),
      }),
    enabled: !!selectedClient?._id,
  });

  // Transform grouped checklists to flat array format for autocomplete
  const checklistOptions = React.useMemo(() => {
    if (
      !checklistData?.groupedChecklists ||
      !Array.isArray(checklistData.groupedChecklists)
    ) {
      return [];
    }
    const checklistArray: any[] = [];

    checklistData.groupedChecklists.forEach((departmentGroup: any) => {
      const departmentName = departmentGroup.departmentName;
      const departmentIcon =
        departmentGroup.departmentId === 'all'
          ? SVG.saveAllIcon
          : departmentGroup.departmentIcon.startsWith('icon')
            ? ICONS.find((icon) => icon.id === departmentGroup.departmentIcon)
                ?.icon
            : getCorrectPath(departmentGroup.departmentIcon, 'image');

      departmentGroup.checklists.forEach((checklist: any) => {
        checklistArray.push({
          id: checklist._id,
          label: checklist.title || `Checklist ${checklist._id}`,
          department: departmentName,
          departmentIcon: departmentIcon,
        });
      });
    });

    return checklistArray;
  }, [checklistData]);

  const checklist =
    forForm === 'announcement'
      ? watch('checklists')
      : watch(`tasks.${index!}.checklists`);

  const checklistFieldName =
    forForm === 'task'
      ? (`tasks.${index}.checklists` as keyof AnnouncementFormValues)
      : 'checklists';

  const isExpanded =
    (forForm == 'announcement' && expandedSections.includes('checklist')) ||
    (forForm == 'task' && expandedSections.includes(`task-checklist-${index}`));

  // Scroll to checklist section when expanded
  useEffect(() => {
    if (isExpanded && checklistRef.current) {
      checklistRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [isExpanded]);

  // Add newly created checklist to selected list
  useEffect(() => {
    if (newChecklistId) {
      const currentChecklists = (checklist as string[]) ?? [];
      if (!currentChecklists.includes(newChecklistId)) {
        setValue(checklistFieldName, [...currentChecklists, newChecklistId], {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
        trigger(checklistFieldName);
      }
      setNewChecklistId(null);
    }
  }, [newChecklistId, checklist, setValue, checklistFieldName, trigger]);

  const toggleDepartment = (department: string) => {
    setExpandedDepartments((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(department)) {
        newSet.delete(department);
      } else {
        newSet.add(department);
      }
      return newSet;
    });
  };

  const handleCheckListRemove = (item: string) => {
    setValue(
      checklistFieldName,
      (checklist as string[]).filter((id) => id !== item),
      {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      },
    );
    trigger(checklistFieldName);
  };

  return (
    <div ref={checklistRef} className="flex flex-col gap-3">
      <div className="flex justify-start">
        <div
          className="flex items-center gap-4 cursor-pointer hover:opacity-80 transition-opacity duration-200"
          onClick={() =>
            handleExpandSection(
              forForm == 'announcement'
                ? 'checklist'
                : `task-checklist-${index}`,
            )
          }
        >
          <div className="w-[24px] h-[24px]">
            <img
              src={SVG.checklistActiveIcon}
              alt="icon"
              className="w-[20px] h-[19px] object-contain"
            />
          </div>
          <p className="m-0 text-[20px] font-medium text-black">
            הוספת צ’קליסט
          </p>
        </div>
      </div>

      {isExpanded && (
        <div className="flex flex-col gap-3">
          <div className="flex items-start gap-4">
            <Controller
              name={checklistFieldName}
              control={control}
              render={({ field: { value } }) => {
                // selected ids
                const selectedIds = new Set<string>((value as string[]) ?? []);

                // pick exactly one representative option per id for the value prop
                const uniqueById = () => {
                  const m = new Map<
                    string,
                    (typeof checklistOptions)[number]
                  >();
                  for (const opt of checklistOptions)
                    if (!m.has(opt.id)) m.set(opt.id, opt);
                  return m;
                };

                return (
                  <AppMUIAutocomplete
                    variant="outlined"
                    disableClearable
                    options={checklistOptions}
                    multiple
                    sx={{
                      width: '100%',
                      maxWidth: '350px',
                      '& .MuiAutocomplete-popupIndicator': {
                        transform: 'none !important', // Disable rotation
                      },
                      '& + .MuiAutocomplete-popper .MuiAutocomplete-listbox > li:hover':
                        {
                          backgroundColor: 'transparent',
                        },
                      '& + .MuiAutocomplete-popper .MuiAutocomplete-listbox > li[aria-selected="true"]:hover':
                        {
                          backgroundColor: 'transparent',
                        },
                    }}
                    popupIcon={<SearchIcon sx={{ color: '#000' }} />}
                    disableCloseOnSelect
                    groupBy={(option) => option.department}
                    getOptionLabel={(option) => option.label}
                    renderOption={(props, option, { selected }) => {
                      const { key, ...optionProps } = props;
                      return (
                        <li
                          key={key}
                          {...optionProps}
                          className="flex items-center gap-2 text-xl cursor-pointer my-[10px] rounded-md hover:bg-hoverBg"
                        >
                          <Checkbox
                            size="medium"
                            color="secondary"
                            checked={selected}
                          />
                          {option.label}
                        </li>
                      );
                    }}
                    renderGroup={(params) => {
                      const icon = checklistOptions.find(
                        (item: any) => item.department === params.group,
                      )?.departmentIcon;
                      const isExpanded = expandedDepartments.has(params.group);
                      const checklistCount = checklistOptions.filter(
                        (item: any) => item.department === params.group,
                      ).length;

                      return (
                        <li className="mx-4 my-4" key={params.key}>
                          <div
                            className={`flex items-center gap-2 cursor-pointer group-header
                              ${
                                isExpanded &&
                                'bg-pastelBlue rounded-md px-[6px] py-2'
                              }`}
                            onClick={() => toggleDepartment(params.group)}
                          >
                            <span>
                              {isExpanded ? (
                                <ArrowDropDownIcon />
                              ) : (
                                <ArrowLeftIcon />
                              )}
                            </span>

                            <img
                              src={icon}
                              alt="icon"
                              className="w-[18px] h-[18px] object-contain"
                            />
                            <span className="text-[20px] text-black">
                              {params.group}
                            </span>
                            <span className="text-[20px] text-black">
                              ({checklistCount})
                            </span>
                          </div>
                          <ul
                            className="p-0"
                            style={{ display: isExpanded ? 'block' : 'none' }}
                          >
                            {params.children}
                          </ul>
                        </li>
                      );
                    }}
                    renderValue={() => ''}
                    isOptionEqualToValue={(opt, val) =>
                      opt.id === (val as any).id
                    }
                    value={
                      Array.from(selectedIds)
                        .map((id) => uniqueById().get(id))
                        .filter(Boolean) as any[]
                    }
                    onChange={(_e, _newValue, _reason, details) => {
                      const id = details?.option?.id as string | undefined;
                      if (!id) return;
                      const next = new Set(selectedIds);
                      next.has(id) ? next.delete(id) : next.add(id);
                      setValue(checklistFieldName, Array.from(next), {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                      trigger(checklistFieldName);
                    }}
                  />
                );
              }}
            />

            {hasPermission('manage_checklists') && (
              <AppButton
                iconStart={
                  <div className="w-[12px] h-[12px]">
                    <img
                      src={SVG.addIcon}
                      alt="icon"
                      className="w-full h-full object-contain"
                    />
                  </div>
                }
                onClick={() => setOpenAddCheckListDialog(true)}
              >
                יצירת צ’קליסט
              </AppButton>
            )}
          </div>

          {checklist?.length > 0 && (
            <div className="py-1 border-y-[1px] border-y-lightDarkGray flex flex-col gap-1">
              {checklist?.map((item: string, index) => {
                const list = checklistOptions.find(
                  (list: any) => list.id === item,
                );

                return (
                  <div key={index} className="flex items-center gap-3">
                    <Checkbox
                      checked={true}
                      color="secondary"
                      size="medium"
                      onChange={() => {
                        handleCheckListRemove(item);
                      }}
                    />
                    <div className="flex items-center gap-2">
                      <div className="w-[18px] h-[18px]">
                        <img
                          src={list?.departmentIcon || SVG.equipmentCardIcon}
                          alt="icon"
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <button
                        className="text-darkBlue underline text-right"
                        onClick={() => setSelectedChecklist(list.id)}
                      >
                        {list?.label}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <AddEditCheckList
        checklistId={selectedChecklist!}
        open={selectedChecklist !== null}
        onClose={() => setSelectedChecklist(null)}
      />
    </div>
  );
}

export default Checklist;
